import React, { useMemo, useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import {
  LineChart, Line,
  BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from "recharts";
import refresh from '../assets/refresh-salespage.svg';
import download from '../assets/download-salespage.svg';
import searchicon from '../assets/search.png';
import files from '../assets/files-salespage.svg';
import users1 from '../assets/users-salespage.svg';
import users from '../assets/users-svgrepo-com.svg';
import rocket from '../assets/rocket-svgrepo-com.svg';
import settings from '../assets/settings-salespage.svg';
import sales from '../assets/sales-up-graph-svgrepo-com.svg';
import box from '../assets/box-minimalistic-svgrepo-com.svg';
import cart from '../assets/cart-large-svgrepo-com.svg';
import medicin from '../assets/medicine-11-svgrepo-com.svg';
import puzzle from '../assets/puzzle-piece-svgrepo-com.svg';
import food from '../assets/grocery-store-canned-svgrepo-com.svg';
import alert from '../assets/alert-salespage.svg';
import { data } from "react-router-dom";

// ---------- Mock Data ----------
const mockClients = [
  {
    id: "c1",
    name: "Delta Mart",
    logo: <img src={cart} className="w-7 h-7"/>,
    region: "EMEA",
    products: [
      {
        id: "p1",
        name: "Delta Soap",
        category: "Home Care",
        price: 35,
        series: genSeries(90, 12000, 24000),
      },
      {
        id: "p2",
        name: "Delta Freshener",
        category: "Home Care",
        price: 55,
        series: genSeries(90, 8000, 19000),
      },
      {
        id: "p3",
        name: "Delta Shampoo",
        category: "Personal Care",
        price: 75,
        series: genSeries(90, 9000, 26000),
      },
    ],
  },
  {
    id: "c2",
    name: "Nova Pharm",
    logo: <img src={medicin} className="w-7 h-7"/>,
    region: "EMEA",
    products: [
      { id: "p4", name: "Nova Vitamin C", category: "Health", price: 120, series: genSeries(90, 11000, 29000) },
      { id: "p5", name: "Nova Probiotic", category: "Health", price: 180, series: genSeries(90, 7000, 21000) },
      { id: "p6", name: "Nova Pain Relief", category: "Health", price: 95, series: genSeries(90, 6000, 20000) },
    ],
  },
  {
    id: "c3",
    name: "Cedar Foods",
    logo: <img src={food} className="w-7 h-7"/>,
    region: "APAC",
    products: [
      { id: "p7", name: "Cedar Olive Oil", category: "Grocery", price: 250, series: genSeries(90, 9000, 30000) },
      { id: "p8", name: "Cedar Pasta", category: "Grocery", price: 45, series: genSeries(90, 4000, 15000) },
      { id: "p9", name: "Cedar Chickpeas", category: "Grocery", price: 38, series: genSeries(90, 5000, 14000) },
    ],
  },
  {
    id: "c4",
    name: "Orbit Tech",
    logo: <img src={puzzle} className="w-7 h-7"/>,
    region: "AMER",
    products: [
      { id: "p10", name: "Orbit Keyboard", category: "Electronics", price: 650, series: genSeries(90, 5000, 23000) },
      { id: "p11", name: "Orbit Mouse", category: "Electronics", price: 350, series: genSeries(90, 6000, 16000) },
      { id: "p12", name: "Orbit Headset", category: "Electronics", price: 520, series: genSeries(90, 8000, 20000) },
    ],
  },
];

function genSeries(days, min, max) {
  const today = new Date();
  return Array.from({ length: days }).map((_, i) => {
    const d = new Date(today);
    d.setDate(d.getDate() - (days - 1 - i));
    const units = Math.floor(rand(min / 100, max / 100));
    const revenue = Math.floor(rand(min, max));
    return {
      date: d.toISOString().slice(0, 10),
      units,
      revenue,
    };
  });
}

function rand(min, max) {
  return Math.random() * (max - min) + min;
}

// ---------- Helpers ----------
const sum = (arr, sel = (x) => x) => arr.reduce((a, b) => a + sel(b), 0);
const fmt = (n) => n.toLocaleString();

function aggregateClient(client, dateFrom, dateTo, categoryFilter) {
  const products = client.products.filter((p) => (categoryFilter ? p.category === categoryFilter : true));
  const productAgg = products.map((p) => {
    const filtered = p.series.filter((s) => s.date >= dateFrom && s.date <= dateTo);
    return {
      productId: p.id,
      productName: p.name,
      category: p.category,
      price: p.price,
      units: sum(filtered, (x) => x.units),
      revenue: sum(filtered, (x) => x.revenue),
      series: filtered,
    };
  });
  const totalRevenue = sum(productAgg, (x) => x.revenue);
  const totalUnits = sum(productAgg, (x) => x.units);
  const bestProduct = productAgg.slice().sort((a, b) => b.revenue - a.revenue)[0];
  return { clientId: client.id, clientName: client.name, logo: client.logo, region: client.region, productAgg, totalRevenue, totalUnits, bestProduct };
}

function aggregateAll(clients, dateFrom, dateTo, categoryFilter) {
  const perClient = clients.map((c) => aggregateClient(c, dateFrom, dateTo, categoryFilter));
  const totalRevenue = sum(perClient, (x) => x.totalRevenue);
  const totalUnits = sum(perClient, (x) => x.totalUnits);
  const activeClients = perClient.filter((x) => x.totalRevenue > 0).length;
  // fastest growing product (simple: compare last 7 days vs previous 7 days)
  const allProducts = perClient.flatMap((c) => c.productAgg.map((p) => ({ ...p, clientName: c.clientName })));
  const fastest = allProducts
    .map((p) => {
      const last7 = p.series.slice(-7);
      const prev7 = p.series.slice(-14, -7);
      const last = sum(last7, (x) => x.revenue);
      const prev = sum(prev7, (x) => x.revenue) || 1;
      return { ...p, growth: (last - prev) / prev };
    })
    .sort((a, b) => b.growth - a.growth)[0];
  return { perClient, totalRevenue, totalUnits, activeClients, fastest };
}

// ---------- Export CSV ----------
function exportCSV(rows, filename = "sales_report.csv") {
  const headers = Object.keys(rows[0] || {});
  const body = rows.map((r) => headers.map((h) => JSON.stringify(r[h] ?? "")).join(",")).join("\n");
  const csv = [headers.join(","), body].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}



// ---------- Component ----------
export default function SalesReportDashboard() {
  const [clients] = useState(mockClients);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [region, setRegion] = useState("");
  const [dateRange, setDateRange] = useState({ from: daysAgo(29), to: today() });
  const [selectedClient, setSelectedClient] = useState(null);
  const [compare, setCompare] = useState([]); // array of client ids
  const [refreshKey, setRefreshKey] = useState(0); // pseudo refresh for mock data

  const categories = useMemo(() => Array.from(new Set(clients.flatMap((c) => c.products.map((p) => p.category)))), [clients]);
  const regions = useMemo(() => Array.from(new Set(clients.map((c) => c.region))), [clients]);

  const filteredClients = useMemo(() => {
    return clients.filter((c) =>
      (region ? c.region === region : true) &&
      (search ? c.name.toLowerCase().includes(search.toLowerCase()) : true)
    );
  }, [clients, search, region]);

  const agg = useMemo(() => aggregateAll(filteredClients, dateRange.from, dateRange.to, category), [filteredClients, dateRange, category, refreshKey]);

  // pick default selected client
  useEffect(() => {
    if (filteredClients.length && (!selectedClient || !filteredClients.find((c) => c.id === selectedClient.id))) {
      setSelectedClient(filteredClients[0]);
    }
  }, [filteredClients]);

  const selectedAgg = useMemo(() => (selectedClient ? aggregateClient(selectedClient, dateRange.from, dateRange.to, category) : null), [selectedClient, dateRange, category, refreshKey]);

  const topClients = useMemo(() => agg.perClient.slice().sort((a, b) => b.totalRevenue - a.totalRevenue).slice(0, 5), [agg]);
  const topProducts = useMemo(() =>
    agg.perClient
      .flatMap((c) => c.productAgg.map((p) => ({ ...p, clientName: c.clientName })))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5),
    [agg]
  );

  function handleExport() {
    const rows = agg.perClient.flatMap((c) =>
      c.productAgg.map((p) => ({
        client: c.clientName,
        product: p.productName,
        category: p.category,
        units: p.units,
        revenue: p.revenue,
      }))
    );
    exportCSV(rows, `sales_report_${dateRange.from}_to_${dateRange.to}.csv`);
  }

  function toggleCompare(clientId) {
    setCompare((prev) => {
      const s = new Set(prev);
      if (s.has(clientId)) s.delete(clientId);
      else if (s.size < 2) s.add(clientId);
      return Array.from(s);
    });
  }

  function refreshMock() {
    // regenerate series to simulate new data
    mockClients.forEach((c) => c.products.forEach((p) => (p.series = genSeries(90, 8000, 30000))));
    setRefreshKey((k) => k + 1);
  }

  const barColors = ["#9F9FF8", "#96E2D6", "#000000", "#92BFFF", "#AEC7ED", "#94E9B8"];
  const pieColors = ["#000000", "#92BFFF", "#94E9B8", "#AEC7ED"];
  return (
    <div className="min-h-screen  text-gray-800">
      {/* Header */}
      <header className="sticky top-0 z-20 backdrop-blur bg-white/70 border-b border-gray-200 dark:bg-[#2D2D2D]/70 dark:border-[#3D3D3D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl agarly-btn-primary text-agarly-dark grid place-items-center text-xl shadow-md">SR</div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight dark:text-gray-200">Sales Report</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Clients • Products • Performance</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={refreshMock} className="dark:text-gray-400 inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-sm hover:bg-gray-50 active:scale-95 transition">
              <span className="text-sm " aria-hidden><img src={refresh} alt="" className="w-4 h-4" /></span> Refresh mock
            </button>
            <button onClick={handleExport} className="inline-flex items-center gap-2 rounded-2xl agarly-btn-primary text-agarly-dark px-4 py-2 text-sm shadow hover:bg-gray-800 active:scale-95 transition">
              <span className="text-sm" aria-hidden><img src={download} alt="" className="w-4 h-4" /></span> Export CSV
            </button>
          </div>
        </div>
      </header>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-4">
            <div className="flex items-center gap-2 rounded-2xl border px-3 py-2 bg-white dark:bg-[#1A1A1B] ">
              <span className="text-gray-500" aria-hidden><img src={searchicon} alt="" className="w-5 h-5" /></span>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search clients..." className="dark:bg-[#1A1A1B] w-full outline-none text-sm" />
            </div>
          </div>
          <div className="md:col-span-3">
            <div className="flex items-center gap-2 rounded-2xl border px-3 py-2 bg-white dark:bg-[#1A1A1B] dark:text-gray-400">
              <span className="text-gray-500" aria-hidden><img src={files} alt="" className="w-5 h-5" /></span>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full outline-none text-sm bg-transparent ">
                <option value="">All categories</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="md:col-span-3">
            <div className="flex items-center gap-2 rounded-2xl border px-3 py-2 bg-white dark:bg-[#1A1A1B] dark:text-gray-400">
              <span className="text-gray-500" aria-hidden><img src={users1} alt="" className="w-5 h-5" /></span>
              <select value={region} onChange={(e) => setRegion(e.target.value)} className="w-full outline-none text-sm bg-transparent">
                <option value="">All regions</option>
                {regions.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 rounded-2xl border px-3 py-2 bg-white dark:bg-[#1A1A1B] dark:text-gray-400">
              <span className="text-gray-500" aria-hidden><img src={settings} alt="" className="w-5 h-5" /></span>
              <select
                value={datePresetFromTo(dateRange)}
                onChange={(e) => setDateRange(parsePreset(e.target.value))}
                className="w-full outline-none text-sm bg-transparent"
              >
                <option value="last30">Last 30 days</option>
                <option value="last60">Last 60 days</option>
                <option value="last90">Last 90 days</option>
                <option value="mtd">Month to date</option>
                <option value="qtd">Quarter to date</option>
                <option value="ytd">Year to date</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* KPIs */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 ">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 ">
          <KPI title="Total Revenue" value={`EGP ${fmt(agg.totalRevenue)}`} icon={<span className="text-base " aria-hidden><img src={sales} alt="" className="w-5 h-5" /></span>} />
          <KPI title="Units Sold" value={fmt(agg.totalUnits)} icon={<span className="text-base" aria-hidden><img src={box} alt="" className="w-6 h-6" /></span>} />
          <KPI title="Top Active Clients" value={fmt(agg.activeClients)} icon={<span className="text-base" aria-hidden><img src={users} alt="" className="w-6 h-6" /></span>} />
          <KPI title="Fastest Growth" value={agg.fastest ? `${agg.fastest.productName} (${agg.fastest.clientName})` : "-"} icon={<span className="text-base" aria-hidden><img src={rocket} alt="" className="w-6 h-6" /></span>} subtle />
        </div>
      </section>

      {/* Main layout */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: clients grid */}
        <section className="lg:col-span-1">
          <h2 className="text-sm font-semibold text-gray-600 mb-2 dark:text-gray-300">Top Clients</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3 ">
            {filteredClients.map((c) => {
              const aggC = aggregateClient(c, dateRange.from, dateRange.to, category);
              const isActive = selectedClient?.id === c.id;
              const inCompare = compare.includes(c.id);
              return (
                <button
                  key={c.id}
                  onClick={() => setSelectedClient(c)}
                  className={`text-left rounded-2xl border bg-white dark:bg-[#1A1A1B] dark:border-[#3D3D3D] p-4 shadow-sm transition ${isActive ? "ring-1 ring-gray-400" : "hover:shadow"}`}
                >
                  <div className="flex items-start justify-between ">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-xl agarly-gradient-bg grid place-items-center text-lg">{c.logo}</div>
                      <div>
                        <p className="font-semibold dark:text-gray-200">{c.name}</p>
                        <p className="text-xs text-gray-700 dark:text-gray-400">{c.region} • {aggC.productAgg.length} products</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCompare(c.id);
                      }}
                      className={`text-xs rounded-full px-2 py-1 border dark:bg-[#1A1A1B] dark:text-gray-300 ${inCompare ? "agarly-btn-primary text-agarly-dark" : "bg-white hover:bg-gray-50"}`}
                    >
                      {inCompare ? "Comparing" : "Compare"}
                    </button>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <p className="text-sm text-gray-500 dark:text-gray-300">Revenue</p>
                    <p className="text-sm font-semibold dark:text-gray-200">EGP {fmt(aggC.totalRevenue)}</p>
                  </div>
                  <MiniSparkline series={aggC.productAgg.flatMap((p) => p.series)} fill="#92BFFF" />
                </button>
              );
            })}
          </div>
        </section>

        {/* Center: client detail */}
        <section className="lg:col-span-2">
          {selectedAgg && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl agarly-btn-primary text-agarly-dark grid place-items-center text-lg">{selectedAgg.logo}</div>
                  <div>
                    <h3 className="font-bold text-lg dark:text-gray-200">{selectedAgg.clientName}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{selectedAgg.region} • Best: {selectedAgg.bestProduct?.productName || "-"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs dark:text-gray-400">
                  <span className="inline-flex items-center gap-1 rounded-full border px-2 py-1"><span className="text-xs" aria-hidden><img src={alert} alt="" className="w-4 h-4" /></span> Smart alerts</span>
                  <span className="inline-flex items-center gap-1 rounded-full border px-2 py-1">{dateRange.from} → {dateRange.to}</span>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="rounded-2xl border bg-white p-4 dark:bg-[#1A1A1B] dark:border-[#3D3D3D]">
                  <h4 className="text-sm font-semibold mb-2 dark:text-gray-300">Revenue by product</h4>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={selectedAgg.productAgg.map((p) => ({ name: p.productName, revenue: p.revenue }))}>
                        <CartesianGrid strokeDasharray="3 3" className="dark:stroke-gray-500" />
                        <XAxis dataKey="name" hide />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="revenue" radius={[8, 8, 0, 0]} barSize={40} fill="#9F9FF8">
                            
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="rounded-2xl border bg-white p-4 dark:bg-[#1A1A1B] dark:border-[#3D3D3D]">
                  <h4 className="text-sm font-semibold mb-2 dark:text-gray-200">Trend over time</h4>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={aggregateSeries(selectedAgg.productAgg)}>
                        <CartesianGrid strokeDasharray="3 3" className="dark:stroke-gray-500"  />
                        <XAxis dataKey="date" hide />
                        <YAxis />
                        <Tooltip />
                        <Line type="monotone" dataKey="revenue" stroke="#92BFFF" dot={false} strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="rounded-2xl border bg-white p-4 lg:col-span-2 dark:bg-[#1A1A1B] dark:border-[#3D3D3D]">
                  <h4 className="text-sm font-semibold mb-3 dark:text-gray-200">Products</h4>
                  <div className="overflow-auto rounded-xl border dark:border-[#3D3D3D]">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-gray-600 dark:bg-black dark:border-[#3D3D3D] dark:text-gray-200">
                        <tr>
                          <th className="text-left p-3">Product</th>
                          <th className="text-left p-3">Category</th>
                          <th className="text-right p-3">Units</th>
                          <th className="text-right p-3">Revenue</th>
                          <th className="text-right p-3">Avg price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedAgg.productAgg.map((p) => (
                          <tr key={p.productId} className="border-t dark:border-[#3D3D3D] dark:hover:bg-[#2D2D2D] dark:text-gray-300">
                            <td className="p-3">{p.productName}</td>
                            <td className="p-3">{p.category}</td>
                            <td className="p-3 text-right">{fmt(p.units)}</td>
                            <td className="p-3 text-right">EGP {fmt(p.revenue)}</td>
                            <td className="p-3 text-right">EGP {fmt(Math.round((p.revenue || 0) / (p.units || 1)))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="rounded-2xl border bg-white p-4 dark:bg-[#1A1A1B] dark:border-[#3D3D3D]">
                  <h4 className="text-sm font-semibold mb-2 dark:text-gray-200">Category mix</h4>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <defs>
                      <linearGradient id="blackGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#000000" />
                        <stop offset="50%" stopColor="#626161ff" />
                        <stop offset="100%" stopColor="#999999ff" />
                      </linearGradient>
                    </defs>
                        <Pie
                          data={categoryMix(selectedAgg.productAgg)}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={55}
                          outerRadius={80}
                          paddingAngle={4}
                          label
                        >
                          {categoryMix(selectedAgg.productAgg).map((_, i) => (
                            <Cell key={i}
                            fill={i === 0 ? "url(#blackGradient)" : pieColors[i % pieColors.length]}
                          stroke="#fff"
                          strokeWidth={2} />
                          ))}
                        </Pie>
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      </main>

      {/* Bottom: leaderboards & compare */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="rounded-2xl border bg-white p-4 dark:bg-[#1A1A1B] dark:border-[#3D3D3D]">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold dark:text-gray-200">Compare clients (max 2)</h4>
            <span className="text-xs text-gray-500 dark:text-gray-400">Select using the list</span>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={compare.map((id) => aggregateClient(clients.find((c) => c.id === id), dateRange.from, dateRange.to, category)).map((c) => ({ name: c.clientName, revenue: c.totalRevenue }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="revenue" radius={[8, 8, 0, 0]} fill="#AEC7ED" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-2xl border bg-white p-4 dark:bg-[#1A1A1B] dark:border-[#3D3D3D]">
          <h4 className="text-sm font-semibold mb-3 dark:text-gray-200">Top clients</h4>
          <ul className="space-y-2">
            {topClients.map((c, idx) => (
              <li key={c.clientId} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="h-6 w-6 grid place-items-center rounded-full border dark:text-gray-300">{idx + 1}</span>
                  <span className="dark:text-gray-300">{c.clientName}</span>
                </div>
                <span className="font-semibold dark:text-gray-200">EGP {fmt(c.totalRevenue)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border bg-white p-4 dark:bg-[#1A1A1B] dark:border-[#3D3D3D]">
          <h4 className="text-sm font-semibold mb-3 dark:text-gray-200">Top products (all clients)</h4>
          <ul className="space-y-2">
            {topProducts.map((p, idx) => (
              <li key={p.productId} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="h-6 w-6 grid place-items-center rounded-full border dark:text-gray-300">{idx + 1}</span>
                  <span className="dark:text-gray-300">{p.productName} <span className="text-gray-500 dark:text-gray-400">• {p.clientName}</span></span>
                </div>
                <span className="font-semibold dark:text-gray-200">EGP {fmt(p.revenue)}</span>
              </li>
            ))}
          </ul>
        </div>
        
      </section>

      <footer className=" text-center text-xs text-gray-500">Last update simulated • Customize freely</footer>

    </div>
  );
}

function KPI({ title, value, icon, subtle = false }) {
  return (
    <div className={`rounded-2xl border p-4 bg-white dark:bg-[#1A1A1B] dark:border-[#3D3D3D] ${subtle ? "opacity-90" : ""}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">{title}</p>
          <p className="text-lg font-bold mt-1 dark:text-gray-200">{value}</p>
        </div>
        <div className="h-10 w-10 rounded-xl agarly-btn-primary text-agarly-dark grid place-items-center">{icon}</div>
      </div>
    </div>
  );
}

function MiniSparkline({ series }) {
  const data = useMemo(() => {
    const daily = {};
    series.forEach((s) => {
      daily[s.date] = (daily[s.date] || 0) + s.revenue;
    });
    return Object.keys(daily).slice(-14).map((d) => ({ date: d, revenue: daily[d] }));
  }, [series]);
  return (
    <div className="h-10 mt-3">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <XAxis dataKey="date" hide />
          <YAxis hide />
          <Tooltip />
          <Line type="monotone" dataKey="revenue" dot={false} strokeWidth={2} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function aggregateSeries(productAgg) {
  const byDate = {};
  productAgg.forEach((p) => {
    p.series.forEach((s) => (byDate[s.date] = (byDate[s.date] || 0) + s.revenue));
  });
  return Object.keys(byDate).map((d) => ({ date: d, revenue: byDate[d] }));
}

function categoryMix(productAgg) {
  const map = {};
  productAgg.forEach((p) => (map[p.category] = (map[p.category] || 0) + p.revenue));
  return Object.entries(map).map(([name, value]) => ({ name, value }));
}

function today() {
  return new Date().toISOString().slice(0, 10);
}
function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function datePresetFromTo(range) {
  const { from, to } = range;
  const diff = (new Date(to) - new Date(from)) / (1000 * 3600 * 24);
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const yearStart = new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10);
  const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1).toISOString().slice(0, 10);
  if (from === daysAgo(29) && to === today()) return "last30";
  if (from === daysAgo(59) && to === today()) return "last60";
  if (from === daysAgo(89) && to === today()) return "last90";
  if (from === monthStart && to === today()) return "mtd";
  if (from === quarterStart && to === today()) return "qtd";
  if (from === yearStart && to === today()) return "ytd";
  return "last30";
}

function parsePreset(value) {
  const now = new Date();
  switch (value) {
    case "last30":
      return { from: daysAgo(29), to: today() };
    case "last60":
      return { from: daysAgo(59), to: today() };
    case "last90":
      return { from: daysAgo(89), to: today() };
    case "mtd": {
      const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
      return { from, to: today() };
    }
    case "qtd": {
      const from = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1).toISOString().slice(0, 10);
      return { from, to: today() };
    }
    case "ytd": {
      const from = new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10);
      return { from, to: today() };
    }
    default:
      return { from: daysAgo(29), to: today() };
  }
}
